import { findQuoteRoot } from "./composeSendBody";
import { aktiveAuswahl, aktivesDokument } from "../windowManager/browserFenster/fensterRegister";

/**
 * Ein- und Ausbau der automatisch eingefügten Signatur im Editor-DOM.
 *
 * Die Anforderung dahinter: Beim Wechsel des Absender-Postfachs soll die
 * Signatur getauscht werden — ohne den geschriebenen Text, das Zitat oder die
 * Cursorposition zu beschädigen. Das ist heikler, als es klingt, deshalb liegt
 * die Logik hier als pures, in jsdom testbares Modul.
 *
 * Zwei Regeln tragen das Ganze:
 *
 *  1. **Nur ein einzelner Knoten wird mutiert.** Würde man `root.innerHTML` als
 *     Ganzes neu schreiben, verlören alle Geschwisterknoten ihre Identität und
 *     JEDE bestehende Selection-Range würde ungültig — der Cursor spränge weg.
 *     Durch die Mutation eines einzelnen Knotens bleiben alle Ranges außerhalb
 *     ohne weiteres Zutun gültig.
 *
 *  2. **Der Marker gilt nur als direktes Kind.** Sonst könnte ein
 *     `data-kmvs-signature` aus zitiertem Fremdinhalt als „unsere" Signatur
 *     durchgehen — und der Austausch würde fremden Text im Zitat überschreiben.
 */

/** Markiert die automatisch eingefügte Signatur. Verlässt nie den Client. */
export const SIG_ATTR = "data-kmvs-signature";

/**
 * Findet den Signaturknoten — ausschließlich unter den direkten Kindern.
 * Siehe Regel 2 im Kopfkommentar.
 */
export function findSignatureNode(root: HTMLElement): HTMLElement | null {
    for (const el of Array.from(root.children)) {
        if (el.hasAttribute(SIG_ATTR)) return el as HTMLElement;
    }
    return null;
}

/**
 * Fügt die Signatur ein: bei einer Antwort OBERHALB des zitierten Verlaufs,
 * sonst am Ende.
 *
 * Die zurückgegebene `baseline` wird bewusst aus dem DOM ZURÜCKGELESEN und
 * nicht der übergebene Rohstring verwendet: Der Browser normalisiert
 * Attributreihenfolge und Anführungszeichen, ein Vergleich gegen den Rohstring
 * würde die Signatur schon beim ersten Vergleich als „bearbeitet" einstufen.
 */
export function insertSignatureNode(
    root: HTMLElement,
    html: string,
): { node: HTMLElement; baseline: string } {
    const node = document.createElement("div");
    node.setAttribute(SIG_ATTR, "1");
    node.innerHTML = html;

    const quote = findQuoteRoot(root);
    if (quote) {
        // Oberhalb des Zitats. `buildReplyQuote` liefert `<br/><br/><div …>`,
        // die Reihenfolge wird also `br, br, [Signatur], Zitat` — oben bleibt
        // der leere Schreibbereich, genau wie in Outlook.
        root.insertBefore(node, quote);
    } else {
        if (root.innerHTML.trim()) root.appendChild(document.createElement("br"));
        root.appendChild(node);
    }

    return { node, baseline: node.innerHTML };
}

/**
 * Tauscht den Inhalt des Signaturknotens aus und gibt die neue Baseline zurück.
 *
 * Stand der Cursor innerhalb der alten Signatur, wird er vor den Knoten
 * gesetzt — andernfalls zeigte die Range in einen Teilbaum, den es nicht mehr
 * gibt, und die nächste Eingabe landete an unvorhersehbarer Stelle.
 */
export function replaceSignatureBody(node: HTMLElement, html: string): string {
    const sel = typeof window !== "undefined" ? aktiveAuswahl() : null;
    const caretInside =
        !!sel &&
        sel.rangeCount > 0 &&
        node.contains(sel.getRangeAt(0).commonAncestorContainer);

    node.innerHTML = html;

    if (caretInside && sel) {
        const r = aktivesDokument().createRange();
        r.setStartBefore(node);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
    }
    return node.innerHTML;
}

/** Entfernt den Signaturknoten (samt vorangehendem Trenner-`<br>`, falls vorhanden). */
export function removeSignatureNode(node: HTMLElement): void {
    const prev = node.previousSibling;
    if (prev && prev.nodeName === "BR") prev.remove();
    node.remove();
}
